import React, { useCallback, useEffect, useState } from "react";
import { OptionCard, Flex, Text, Input, Notice } from "@thenounproject/lingo-core";

import ModalHeader from "../../../../app/_components/ModalHeader";
import ModalBody from "../../../../app/_components/ModalBody";
import ModalFooter from "../../../../app/_components/ModalFooter";

import useShowModal from "@redux/actions/useModals";
import useDuplicateKit from "@redux/actions/kits/useDuplicateKit";
import useNotifications from "@redux/actions/useNotifications";
import useKit from "@actions/kits/useKit";

type Props = {
  kitId: string;
  spaceId: string | number;
};

const DuplicateKitModal: React.FC<Props> = ({ kitId, spaceId }) => {
  const { data: kit } = useKit({ kitId, spaceId }),
    [createReferences, setCreateReferences] = useState(true),
    { dismissModal } = useShowModal(),
    { showNotification } = useNotifications(),
    [kitName, setKitName] = useState<string>(null);

  const [duplicateKit, { isProcessing, error }] = useDuplicateKit();

  useEffect(() => {
    if (kitName === null && kit?.name) {
      setKitName(kit.name);
    }
  }, [kit?.name, kitName]);

  const submit = useCallback(async () => {
    const { isSuccess } = await duplicateKit({
      kitId,
      spaceId,
      createReferences,
      name: kitName,
    });

    if (isSuccess) {
      showNotification({
        message: "Your kit is being duplicated. It may take up to a few minutes.",
      });
      dismissModal();
    }
  }, [duplicateKit, kitId, spaceId, createReferences, kitName, dismissModal, showNotification]);

  return (
    <>
      <ModalHeader title="Duplicate Kit" />
      <ModalBody>
        <Flex flexDirection="column" gap="s">
          <Input
            label="Kit Name"
            placeholder="e.g. Style Guide, UI Library"
            value={kitName}
            onChange={e => setKitName(e.target.value)}
          />
          <Flex flexDirection="column">
            <Text mt="m" font="ui.smallBold">
              Assets
            </Text>
            <Text>How would you like to copy assets to the new kit?</Text>
          </Flex>
          <OptionCard
            cardStyle="radio"
            selected={createReferences}
            onClick={() => setCreateReferences(true)}
            title="Create references"
            detail="Reference the original assets. Changes to assets will be reflected in both kits."
          />
          <OptionCard
            cardStyle="radio"
            selected={!createReferences}
            onClick={() => setCreateReferences(false)}
            title="Duplicate assets"
            detail="Create independent copies of all assets in the new kit."
          />
          {error && <Notice noticeStyle="error" message={error.message} />}
        </Flex>
      </ModalBody>
      <ModalFooter
        primary={{
          text: isProcessing ? "Duplicating Kit..." : "Duplicate Kit",
          onClick: submit,
          disabled: isProcessing,
        }}
      />
    </>
  );
};

export default DuplicateKitModal;
